import { helper } from './helper';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import { osName } from 'react-device-detect';
import moment from 'moment';
import 'moment/locale/zh-cn';
import 'moment/locale/en-sg';
import { Address, PaletteMode } from './types';

class UIHelper {

  static regExp = {
    email: '^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$',
    nric: '^[S|T]\\d{3}[J|Z|I|H|G|F|E|D|C|B|A]|[F|G]\\d{3}[X|W|U|T|R|Q|P|N|M|L|K]$',
    amount: '^\\d+(\\.\\d{1,2})?$',
    positiveAmt: '^\\s*(?=.*[1-9])\\d*(?:\\.\\d{1,2})?\\s*$',
    website: '^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$',
    positiveInt: '^[1-9]\\d*$',
    integer: '^-?\\d+$'
  };

  static getLocale(appUI : any) {
    return appUI && appUI.isSuccess ? helper.carefullyGetStringValue(appUI.data, ['lang'], 'zh-CN') : 'zh-CN'; 
  }

  static getDisplayModeSetting(prefersDarkMode: boolean, appUI : any) {
    const displayMode : PaletteMode = appUI.isSuccess ? (helper.carefullyGetStringValue(appUI.data, ['displayMode'], 'auto') as PaletteMode) : 'auto';
    const displayModeToUse : PaletteMode = displayMode === 'auto' ? (prefersDarkMode ? 'dark' : 'light') : displayMode;
    return {displayMode, displayModeToUse};
  }

  static getLoggingConfig(logConfig : any) {
    /*
    "log_enable": true,
    "max_log_cap": 20,
    "sync_interval": 30000,
    "retry_interval": 60000,
    "sync_batchsize": 10,
    "console_log_enable": true
    */
    const logEnabled = helper.carefullyGetBooleanValue(logConfig, 'value.log_enable'.split('.'), false);
    const consoleLogEnabled = helper.carefullyGetBooleanValue(logConfig, 'value.console_log_enable'.split('.'), false);
    const maxLogCap = helper.carefullyGetIntValue(logConfig, 'value.max_log_cap'.split('.'), 100);
    const syncInterval = helper.carefullyGetIntValue(logConfig, 'value.sync_interval'.split('.'), 30000);
    const retryInterval = helper.carefullyGetIntValue(logConfig, 'value.retry_interval'.split('.'), 60000);
    const syncBatchsize = helper.carefullyGetIntValue(logConfig, 'value.sync_batchsize'.split('.'), 20);

    return {logEnabled, consoleLogEnabled, maxLogCap, syncInterval, retryInterval, syncBatchsize};
  }

  static reactDataGridI18n(t : (key : string) => string) {
    const i18n = Object.assign({}, ReactDataGrid.defaultProps.i18n, {
      pageText: t('datagrid.pagination.pageText'),
      ofText: t('datagrid.pagination.ofText'),
      perPageText: t('datagrid.pagination.perPageText'),
      showingText: t('datagrid.pagination.showingText'),
      clearAll: t('datagrid.filter.clearAll'),
      clear: t('datagrid.filter.clear'),
      showFilteringRow: t('datagrid.filter.showFilteringRow'),
      hideFilteringRow: t('datagrid.filter.hideFilteringRow'),
      dragHeaderToGroup: t('datagrid.group.dragHeader'),
      disable: t('datagrid.filter.disable'),
      enable: t('datagrid.filter.enable'),
      sortAsc: t('datagrid.sort.asc'),
      sortDesc: t('datagrid.sort.desc'),
      unsort: t('datagrid.sort.unsort'),
      group: t('datagrid.group.group'),
      ungroup: t('datagrid.group.ungroup'),
      lockStart: t('datagrid.lock.start'),
      lockEnd: t('datagrid.lock.end'),
      unlock: t('datagrid.lock.unlock'),
      columns: t('datagrid.columns'),
      contains: t('datagrid.filter.contains'),
      startsWith: t('datagrid.filter.startsWith'),
      endsWith: t('datagrid.filter.endsWith'),
      notContains: t('datagrid.filter.notContains'),
      neq: t('datagrid.filter.neq'),
      eq: t('datagrid.filter.eq'),
      notEmpty: t('datagrid.filter.notEmpty'),
      empty: t('datagrid.filter.empty'),
      lt: t('datagrid.filter.lt'),
      lte: t('datagrid.filter.lte'),
      gt: t('datagrid.filter.gt'),
      gte: t('datagrid.filter.gte'),
      inlist: t('datagrid.filter.inlist'),
      notinlist: t('datagrid.filter.notinlist'),
      inrange: t('datagrid.filter.inrange'),
      notinrange: t('datagrid.filter.notinrange'),
      before: t('datagrid.filter.before'),
      beforeOrOn: t('datagrid.filter.beforeOrOn'),
      afterOrOn: t('datagrid.filter.afterOrOn'),
      after: t('datagrid.filter.after'),
      start: t('datagrid.filter.start'),
      end: t('datagrid.filter.end'),
      autoresizeThisColumn: t('datagrid.autoresizeThisColumn'),
      autoresizeAllColumns: t('datagrid.autoresizeAllColumns'),
      autoSizeToFit: t('datagrid.autoSizeToFit'),
      noRecords: t('datagrid.noRecords'),
      'calendar.todayButtonText': t('datagrid.calendar.todayButtonText'),
      'calendar.clearButtonText': t('datagrid.calendar.clearButtonText'),
      'calendar.okButtonText': t('datagrid.calendar.okButtonText'),
      'calendar.cancelButtonText': t('datagrid.calendar.cancelButtonText')
    })

    return i18n;
  }

  static getCodeText(code : string, codeList : any[], defaultValue : string='') {
    let codeText = defaultValue;

    if(helper.stringHasValue(code) && helper.arrayHasItem(codeList)) {
      let codeItem = codeList.find(item => item.code === code);
      if(helper.isNotNullAndUndefined(codeItem)) {
        codeText = codeItem.text;
      }
    }
    return codeText;
  }

  static getRoleText(role : string, roleList : any[], lang : string, defaultValue : string='') {
    let roleText = defaultValue;

    if(helper.stringHasValue(role) && helper.arrayHasItem(roleList)) {
      let roleItem = roleList.find(item => item.role === role);
      
      if(helper.isNotNullAndUndefined(roleItem) && helper.arrayHasItem(roleItem.value?.name)) {
        let roleLangName = roleItem.value.name.find((nameItem : any) => nameItem.lang === lang);
        if(helper.isNotNullAndUndefined(roleLangName)) {
          roleText = roleLangName.text;
        }
      }
    }
    return roleText;
  }

  static getModifierKey() {
    return osName === 'Mac OS' ? 'Cmd' : 'Ctrl';
  }

  static populateDDLNumberOptions(start : number, step : number, count : number, textModifier? : (code : number) => string) {
    let options = [];

    for(let idx=0; idx < count; idx++) {
      let codeValue = start + (step * idx);
      options.push(
        {
          code: codeValue,
          text: textModifier ? textModifier(codeValue) : codeValue.toString()
        }
      );
    }

    return options;
  }

  static formGetCheckBoxStatus(formRef : any, elementId : any, defaultStatus : boolean=false) {
    let status = defaultStatus;
    if(helper.isNotNullAndUndefined(formRef?.current?.elements)) {
      if(helper.isNotNullAndUndefined(formRef.current.elements[elementId])) {
        status = helper.carefullyGetBooleanValue(formRef, `current.elements.${elementId}.checked`.split('.'), defaultStatus);
      }
    }

    return status;
  }

  static getObjFieldLangText(object : any, field : string, locale='en-US') {
    let text = '';

    if(helper.isNotNullAndUndefined(object) && helper.stringHasValue(field)) {
      if(locale === 'en-US') {
        text = object.hasOwnProperty(field) ? object[field] : '';
      }
      else {
        if(helper.arrayHasItem(object.translations)) {
          let trans = object.translations.find((item : any) => item.languages_code === locale);
          if(helper.isNotNullAndUndefined(trans) && trans.hasOwnProperty(field)) {
            text = trans[field];
          }
        }
      }
    }

    return text;
  }

  // static formatDate(date, lang='en-sg', format='LLL', timeZoned=true) {
  //   moment.locale(lang.toLowerCase()); 
    
  //   if(date !== undefined && date !== null) {
  //     var value = timeZoned ? moment(date) : moment(date).utc();
  //     return value.format(format);
  //   }
  //   else
  //     return '';
  // }

  /**
   * @param [format='LLL'] Refer to https://momentjscom.readthedocs.io/en/latest/moment/04-displaying/01-format/
   * 
   * Time	LT	8:30 PM
   * Time with seconds	LTS	8:30:25 PM
   * Month numeral, day of month, year	L	09/04/1986
   *                                    l	9/4/1986
   * Month name, day of month, year	LL	September 4, 1986
   *                                ll	Sep 4, 1986
   * Month name, day of month, year, time	LLL	September 4, 1986 8:30 PM
   *                                      lll	Sep 4, 1986 8:30 PM
   * Month name, day of month, day of week, year, time	LLLL	Thursday, September 4, 1986 8:30 PM
   *                                                    llll	Thu, Sep 4, 1986 8:30 PM    
   */
  static formatDate(date : any, lang='en-sg', format='LLL') : string {
    moment.locale(lang.toLowerCase()); 
    
    if(date !== undefined && date !== null) {
      if(typeof date === 'string') {
        if(helper.stringHasValue(date)) {
          let dateStr = date;
          if(dateStr[dateStr.length - 1].toUpperCase() !== 'Z') {
            dateStr += 'Z';
          }
          return moment.utc(date).local().format(format);
        }
        else
          return '';
      }
      else {
        return moment.utc(date).local().format(format);
      }
    }
    else
      return '';
  }

  static makeFullName(firstName : string | null, lastName : string | null) {
    let fullName = '';

    if(helper.stringHasValue(firstName))
      fullName = firstName;

    if(helper.stringHasValue(lastName)) {
      fullName += ' ' + lastName;
    }

    return fullName;
  }

  static getLocaleText(englishText : string, chineseText : string, locale : string = 'zh-CN') {
    let result = '';

    if(locale === 'zh-CN') {
      result = chineseText;
    }

    if(!helper.stringHasValue(result)) {
      result = englishText;
    }

    return result;
  }

  static makeFullAddress(address : Address, countries : any [], multiLine : boolean = false) {
    let fullAddress : any = '';
    
    if(!helper.isNotNullAndUndefined(address))
      return fullAddress;

    let country = address.country;

    if(helper.stringHasValue(country) && helper.arrayHasItem(countries)) {
      let code = countries.find(item => item.code === country);
      if(helper.isNotNullAndUndefined(code)) {
        country = code.text;
      }
    }

    if(helper.stringHasValue(address.house_no))
      fullAddress = address.house_no;

    if(helper.stringHasValue(address.street)) {
      fullAddress += (fullAddress.length > 0 ? ' ' : '') + address.street;
    }

    if(helper.stringHasValue(address.building)) {
      fullAddress += (fullAddress.length > 0 ? ', ' + (multiLine ? '\n' : '') : '') + address.building;
    }

    if(helper.stringHasValue(address.unit)) {
      fullAddress += (fullAddress.length > 0 ? ', ' + (multiLine ? '\n' : '') : '') + address.unit;
    }

    if(helper.stringHasValue(country)) {
      fullAddress += (fullAddress.length > 0 ? ', ' + (multiLine ? '\n' : '') : '') + country;
    }

    if(helper.stringHasValue(address.postal_code)) {
      fullAddress += (fullAddress.length > 0 ? ' ' : '') + address.postal_code;
    }

    return fullAddress;
  }

  static makeFullAddressLines(address : Address, countries : any []) {
    let line1 : any = '';
    let line2 : any = '';
    let country = address?.country;

    if(!helper.isNotNullAndUndefined(address))
      return {line1, line2};

    if(helper.stringHasValue(country)) {
      let code = countries.find(item => item.code === country);
      if(helper.isNotNullAndUndefined(code)) {
        country = code.text;
      }
    }

    if(helper.stringHasValue(address.house_no))
      line1 = address.house_no;

    if(helper.stringHasValue(address.street)) {
      line1 += (line1.length > 0 ? ' ' : '') + address.street;
    }

    if(helper.stringHasValue(address.building)) {
      line2 += (line2.length > 0 ? ', ' : '') + address.building;
    }

    if(helper.stringHasValue(address.unit)) {
      line2 += (line2.length > 0 ? ', ' : '') + address.unit;
    }

    if(helper.stringHasValue(country)) {
      line2 += (line2.length > 0 ? ', ' : '') + country;
    }

    if(helper.stringHasValue(address.postal_code)) {
      line2 += (line2.length > 0 ? ' ' : '') + address.postal_code;
    }

    return {line1, line2};
  }

  static maskNric(nric : string) {
    let maskedNric = '';

    if(helper.stringHasValue(nric)) {
      if(isNaN(Number.parseInt(nric[0]))) {
        maskedNric = nric[0];
        maskedNric += `****${nric.substring(1)}`;
      }
      else {
        maskedNric = `****${nric}`;
      }
    }
    return maskedNric.toUpperCase();
  }

  static breakNric(nric : string) {
    let nricParts = {
      prefix: '',
      last3: '',
      postfix: ''
    };

    if(helper.stringHasValue(nric)) {
      nric = nric.replace(/\s/g, '').toUpperCase();
      if(nric.length > 0 && isNaN(Number.parseInt(nric[0]))) {
        nricParts.prefix = nric[0];
      }

      if(nric.length > 1) {
        let last3 = nric.substring(1, 4);
        if(!isNaN(Number.parseInt(last3))) {
          nricParts.last3 = last3;
        }
      }

      if(nric.length > 4 && isNaN(Number.parseInt(nric[4]))) {
        nricParts.postfix = nric[4];
      }
      
    }
    return nricParts;
  }

  static isFormDirty(custValidation : any) : boolean {
    // Check if form is dirty?
    let touched = false;
    let keys = Object.keys(custValidation);
    if(helper.arrayHasItem(keys)) {
      keys.every(key => {
        if(custValidation[key].touched) {
          touched = true;
          return false; // Dirty field detected, return false to break the every loop
        }

        // return true if need to check the next key
        return true;
      });
    }
    
    return touched;
  }

  static formatCurrency(amount : number, decimalPlace=2, sign='$', groupSize=3) {
    if(amount !== undefined && amount !== null) {
      var re = '\\d(?=(\\d{' + (groupSize || 3) + '})+' + (decimalPlace > 0 ? '\\.' : '$') + ')';
      if(amount >= 0)
        return sign + amount.toFixed(Math.max(0, ~~decimalPlace)).replace(new RegExp(re, 'g'), '$&,');
      else {
        return '-' + sign + Math.abs(amount).toFixed(Math.max(0, ~~decimalPlace)).replace(new RegExp(re, 'g'), '$&,');
      }
    }
    else
      return '';
  }

  static switchCartCustomer(userCartSetting : any, newCustomerId : string, stateSetter : any) {
    if(helper.isNotNullAndUndefined(userCartSetting)) {
      let localUserCartSetting = helper.deepCopy(userCartSetting);
      if(!helper.isNotNullAndUndefined(localUserCartSetting.value)) {
        localUserCartSetting.value = {};
      }
      localUserCartSetting.value.customer = newCustomerId;
      // Remove the user_created and date_created
      delete localUserCartSetting.user_created;
      delete localUserCartSetting.date_created;
      stateSetter && stateSetter.mutate(localUserCartSetting);
    }
    else {
      let localUserCartSetting = {
        application: process.env.APP_ID,
        category: 'cashier',
        identifier: 'cart.state',
        value: {
          customer: newCustomerId
        }
      };

      stateSetter && stateSetter.mutate(localUserCartSetting);
    }
  }

  static populateCustomerName = (customer : any, memberTypes: any[]) : string => {
    let name = '';

    if(helper.isNotNullAndUndefined(customer)) {
      name = customer.english_name;
      if(name.length > 0) {
        if(helper.stringHasValue(customer.chinese_name)) {
          name += ` (${customer.chinese_name})`
        }
      }
      else {
        name = customer.chinese_name;
      }

      let memType = memberTypes.find(item => item.code === customer.member_type);
      if(helper.isNotNullAndUndefined(memType)) {
        name += ` - ${memType.text}`;

        if(customer.member_type === 'none') {
          name += `: ${customer.devotee_serial}`;
        }
        else {
          name += `: ${customer.member_serial}`
        }
      }
    }

    return name;
  }

  static populateCustomerType = (customer : any, memberTypes: any[]) : string => {
    let name = '';

    if(helper.isNotNullAndUndefined(customer)) {
      let memType = memberTypes.find(item => item.code === customer.member_type);
      if(helper.isNotNullAndUndefined(memType)) {
        name += memType.text;

        if(customer.member_type === 'none') {
          name += `: ${customer.devotee_serial}`;
        }
        else {
          name += `: ${customer.member_serial}`
        }
      }
    }

    return name;
  }

  // static calculateFontSize = (fontSize : number, lineHeight : number, fontFamily : string, canvasSize: any, orientation : string, text : string) : number => {
  //   let adjustedFontSize = fontSize;

  //   let rawText = text;

  //   do {
  //     let calWidth = UIHelper.getTextwidth(rawText, fontFamily, adjustedFontSize);
  //     // console.log('calculateFontSize: ', calWidth, lineHeight);
  //     if(orientation === 'vertical' && UIHelper.isChineseChar(text)) {
  //       // If orientation is vertical and is chinese character, need to include the lineHeight
  //       let tempWidth = calWidth / text.length;
  //       calWidth = calWidth * lineHeight; // + ((lineHeight - 1) * tempWidth * (text.length - 1));
  //       // console.log('calculateFontSize adjusted: ', calWidth, lineHeight);
  //     }

  //     if(calWidth > (orientation === 'vertical' ? canvasSize.height : canvasSize.width)) {
  //       adjustedFontSize--;
  //     }
  //     else
  //       break;

  //   } while (true)

  //   if(adjustedFontSize <= 0) {
  //     adjustedFontSize = 1;
  //   }
    
  //   return adjustedFontSize;
  // }

  static getMaxStringLine(inputStr : string) : string {
    let result = '';

    if(helper.stringHasValue(inputStr)) {
      let inputLines = inputStr.split('\n');
      
      for(let lineIdx=0; lineIdx<inputLines.length; lineIdx++) {
        let line = inputLines[lineIdx];
        
        if(line.length > result.length) {
          result = line;
        }
      }
    }

    return result;
  }

  static calculateFontSize = (fontSize : number, lineHeight : number, fontFamily : string, canvasSize: any, orientation : string, text : string) : number => {
    let adjustedFontSize = fontSize;

    // let rawText = text;
    let textLines = text.split('\n');

    do {
      let calWidth = UIHelper.getTextwidth(UIHelper.getMaxStringLine(text), fontFamily, adjustedFontSize);
      // console.log('calculateFontSize: ', calWidth, lineHeight);
      if(orientation === 'vertical' && UIHelper.isChineseChar(text)) {
        // If orientation is vertical and is chinese character, need to include the lineHeight
        let tempWidth = calWidth / text.length;
        calWidth = calWidth * lineHeight; // + ((lineHeight - 1) * tempWidth * (text.length - 1));
        // console.log('calculateFontSize adjusted: ', calWidth, lineHeight);
      }

      if(calWidth > (orientation === 'vertical' ? canvasSize.height : canvasSize.width)) {
        adjustedFontSize--;
      }
      else {
        if(textLines.length > 1) {
          // If have multiple lines, also check the width of the lines in total
          let multiLineFirstLine = '';
          textLines.forEach(line => {
            multiLineFirstLine += line.length > 0 ? line[0] : ' ';
          });

          let linesWidth = UIHelper.getTextwidth(multiLineFirstLine, fontFamily, adjustedFontSize);
          if(linesWidth > (orientation === 'vertical' ? canvasSize.width : canvasSize.height)) {
            adjustedFontSize--;
          }
          else {
            break;
          }
        }
        else {
          // If only one line, and calWidth is within the canvasSize, break the loop
          break;
        }
      }
    } while (true)

    if(adjustedFontSize <= 0) {
      adjustedFontSize = 1;
    }
    
    return adjustedFontSize;
  }

  static getTextwidth(text : string, font : string, fontSize : number) {
    let width = 0;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if(context) {
      context.font = `${fontSize}px ${font}`;
      width = context.measureText(text).width;
    }

    return width * 1.1; // multiply 1.1 is to increase the calculated with by 10% to cater for errors
  }

  static isChineseChar(text : string) {
    const regExp = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/g;
    return regExp.test(text);
  }

  static toTitleCase(text : string) {
    return text.replace(/(^\w|\s\w)/g, m => m.toUpperCase());
  }

  static getFieldValue(mode : string, conditions : {mode : string, value : string}[], defaultValue : string) : string {
    let returnValue = defaultValue;

    if(helper.arrayHasItem(conditions)) {
      let condition = conditions.find(item => item.mode === mode);
      if(helper.isNotNullAndUndefined(condition)) {
        returnValue = condition.value;
      }
    }

    return returnValue;
  }

  static getFieldBooleanValue(mode : string, conditions : {mode : string, value : boolean}[], defaultValue : boolean) : boolean {
    let returnValue = defaultValue;

    if(helper.arrayHasItem(conditions)) {
      let condition = conditions.find(item => item.mode === mode);
      if(helper.isNotNullAndUndefined(condition)) {
        returnValue = condition.value;
      }
    }

    return returnValue;
  }
}

export default UIHelper;